import type { WorkflowSelectedValues } from "./choiceModel";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "../../components/ui";
import { DocumentationNoteDateTime, stockholmDateTime } from "./DocumentationNoteView";
import { DynamicDocumentationForm } from "./DynamicDocumentationForm";
import type { DocumentationDraft } from "./models";
import type { WorkflowNodeEdits } from "./workflowNodeResultUtils";
import { WorkflowNodeResults } from "./WorkflowNodeResults";

import styles from "./DocumentationWorkspace.module.css";

type Props = {
    draft: DocumentationDraft;
    selectedWorkflowId: string;
    fieldEdits: Record<string, unknown>;
    fieldConfirmations: Set<string>;
    nodeEdits: WorkflowNodeEdits;
    selectedValues?: WorkflowSelectedValues;
    onChoiceEdit?: (nodeId: string, value: string, content: string) => void;
    nodeConfirmations: Set<string>;
    disabled: boolean;
    allowWorkflowSelection?: boolean;
    workflowChangeHint?: string;
    sourceNotes?: Array<{ note_id: string; title: string; timestamp?: number | null }>;
    onWorkflowSelection: (workflowId: string) => void;
    onFieldEdit: (fieldKey: string, value: unknown) => void;
    onFieldConfirmation: (fieldKey: string, confirmed: boolean) => void;
    onNodeEdit: (nodeId: string, content: string) => void;
    onNodeConfirmation: (nodeId: string, confirmed: boolean) => void;
    onSourceNoteClick?: (noteId: string, projectId?: string) => void;
};

export const DocumentationCardEditor = ({
    draft,
    selectedWorkflowId,
    fieldEdits,
    fieldConfirmations,
    nodeEdits,
    selectedValues,
    onChoiceEdit,
    nodeConfirmations,
    disabled,
    allowWorkflowSelection = true,
    workflowChangeHint,
    sourceNotes,
    onWorkflowSelection,
    onFieldEdit,
    onFieldConfirmation,
    onNodeEdit,
    onNodeConfirmation,
    onSourceNoteClick
}: Props) => {
    const { t } = useTranslation();
    const workflowChanged = Boolean(selectedWorkflowId && selectedWorkflowId !== draft.selected_workflow?.workflow_id);
    const globalIssues = useMemo(() => (draft.validation?.issues || []).filter(issue => !issue.field_key), [draft.validation?.issues]);

    return (
        <div>
            {allowWorkflowSelection && (
                <label className={styles.workflowSelector}>
                    <span>{t("documentFlow.workflow")}</span>
                    <Select value={selectedWorkflowId} disabled={disabled} onChange={event => onWorkflowSelection(event.target.value)}>
                        {(draft.workflow_options || []).map(option => (
                            <option key={option.id} value={option.id}>
                                {option.title}
                            </option>
                        ))}
                    </Select>
                    {workflowChanged ? <small className={styles.workflowChangeHint}>{workflowChangeHint ?? t("documentFlow.workflowChangeHint")}</small> : null}
                </label>
            )}

            <DocumentationNoteDateTime {...stockholmDateTime(draft.created_at)} />
            <WorkflowNodeResults
                goals={draft.context_options?.active_goals}
                nodes={draft.workflow_node_results}
                edits={nodeEdits}
                selectedValues={selectedValues}
                onChoiceEdit={onChoiceEdit}
                confirmations={nodeConfirmations}
                sourceNotes={sourceNotes || draft.context_options?.signed_notes}
                disabled={disabled || workflowChanged}
                onSourceNoteClick={onSourceNoteClick}
                onEdit={onNodeEdit}
                onConfirmation={onNodeConfirmation}
            />

            {draft.definition?.fields.length ? (
                <section className={styles.supportingDetails} aria-labelledby={`${draft.id}-supporting-details`}>
                    <h3 id={`${draft.id}-supporting-details`}>{t("documentFlow.supportingDetails")}</h3>
                    <DynamicDocumentationForm
                        draft={draft}
                        edits={fieldEdits}
                        confirmations={fieldConfirmations}
                        disabled={disabled || workflowChanged}
                        onEdit={onFieldEdit}
                        onConfirmation={onFieldConfirmation}
                    />
                </section>
            ) : null}

            {globalIssues.length ? (
                <div className={styles.validationSummary}>
                    <div>
                        {globalIssues.map(issue => (
                            <p key={`${issue.rule_key}-${issue.code}`} className={issue.severity === "error" ? styles.fieldError : styles.fieldWarning}>
                                {issue.message}
                            </p>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
